import {
  useEffect,
  useLayoutEffect,
  useId,
  useRef,
  useState,
  type ClipboardEvent,
  type ReactElement,
} from "react";
import { Plus, Trash2, Upload } from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { RunChartParams } from "@playbookai/widget-schemas";
import {
  widgetTargetKey,
  rebaseWidgetParams,
} from "@playbookai/widget-schemas";
import { cn } from "../../lib/utils.js";
import { Button } from "../../atoms/Button/Button.js";
import { KPIValue } from "../../atoms/KPIValue/index.js";
import { useReducedMotion } from "../../hooks/useReducedMotion.js";
import { ChartTooltip } from "../../molecules/ChartTooltip/index.js";
import { DataTable } from "../../molecules/DataTable/index.js";
import { Tooltip as DsTooltip } from "../../molecules/Tooltip/Tooltip.js";
import { newWidgetItemId } from "../widget-item-id.js";
import { LeasedInput } from "../LeasedInput.js";
import {
  RunChartTargetDot,
  RunChartTargetGradient,
  runChartTargetState,
} from "./Component.js";

interface ParsedSheet {
  readonly headers: string[];
  readonly rows: ReadonlyArray<Record<string, string | number>>;
  readonly fileName: string;
}

export function RunChartExpandedEditor({
  data,
  onChange,
  readOnly,
}: {
  readonly data: RunChartParams;
  readonly onChange: (next: unknown) => void;
  readonly readOnly?: boolean | undefined;
}): ReactElement {
  const [draft, setDraft] = useState<RunChartParams>(() => ({
    title: data.title,
    unit: data.unit ?? "",
    target: data.target,
    points: data.points.map((point) => ({ ...point })),
  }));
  const [parsed, setParsed] = useState<ParsedSheet | null>(null);
  const [labelCol, setLabelCol] = useState("");
  const [valueCol, setValueCol] = useState("");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const onChangeRef = useRef(onChange);
  // `draftRef` mirrors the latest draft; `committedRef` holds the last value
  // sent to onChange (seeded with the initial draft). An edit is "pending" when
  // they differ. draftRef is updated in a *layout* effect — synchronously after
  // each render, before the next event or unmount — so the unmount flush below
  // always reads the latest draft rather than a value one render stale.
  const draftRef = useRef(draft);
  const committedRef = useRef(draft);
  // Sync both refs in layout effects (synchronous, before the next event or
  // unmount) so the unmount flush below always reads the newest callback and
  // draft — never a value left one render stale by a passive effect.
  useLayoutEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useLayoutEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    if (readOnly) return undefined;
    // Skip when nothing changed — including the initial mount, where `draft`
    // and `committedRef` still share the seed object.
    if (draft === committedRef.current) return undefined;
    const handle = window.setTimeout(() => {
      committedRef.current = draft;
      onChangeRef.current(draft);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [draft, readOnly]);

  // Flush a still-pending edit on unmount so closing the modal within the
  // 250 ms debounce window doesn't silently drop the last change.
  useEffect(() => {
    return () => {
      if (draftRef.current !== committedRef.current) {
        onChangeRef.current(draftRef.current);
      }
    };
  }, []);

  // Rebase pending local intent as remote values arrive; unrelated fields must
  // reach the draft even inside its debounce window.
  const serverSignature = JSON.stringify(data);
  useEffect(() => {
    // Read-only adopts too, and has to: with nothing local to protect, a viewer
    // watching this widget would otherwise sit on the content as it was when
    // they opened it.
    const adopted: RunChartParams = {
      title: data.title,
      unit: data.unit ?? "",
      target: data.target,
      points: data.points.map((point) => ({ ...point })),
    };
    if (JSON.stringify(adopted) === JSON.stringify(draftRef.current)) return;
    const rebased = rebaseWidgetParams(
      "runChart",
      committedRef.current,
      draftRef.current,
      adopted,
    ) as RunChartParams;
    committedRef.current = adopted;
    draftRef.current = rebased;
    setDraft(rebased);
    /* Keyed on the SIGNATURE, not the object: `data` is a fresh object on
       every render of the host, so depending on it — or on its four fields,
       which `serverSignature` already covers — would re-run this every render
       and adopt over the draft the guard above exists to protect.

       The directive must sit on the line IMMEDIATELY above the dependency
       array. It was previously the first line of a multi-line `//` comment, so
       it disabled the comment line beneath it and did nothing. */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [serverSignature]);

  const lastPoint = draft.points[draft.points.length - 1];
  const targetState = runChartTargetState(lastPoint?.value, draft.target);

  const updatePoint = (
    index: number,
    patch: Partial<RunChartParams["points"][number]>,
  ): void => {
    setDraft((current) => ({
      ...current,
      points: current.points.map((point, i) =>
        i === index ? { ...point, ...patch } : point,
      ),
    }));
  };

  const addRow = (): void => {
    setDraft((current) => ({
      ...current,
      points: [
        ...current.points,
        {
          // PB-4923: a point without an id cannot be addressed by a bounded
          // operation, and the label is NOT the identity — it is ordinary,
          // editable content that may legitimately repeat across a series.
          id: newWidgetItemId("point"),
          label:
            current.points.length === 0
              ? "P1"
              : `P${current.points.length + 1}`,
          value: 0,
        },
      ],
    }));
  };

  const deleteRow = (index: number): void => {
    setDraft((current) => ({
      ...current,
      points: current.points.filter((_, i) => i !== index),
    }));
  };

  const handleFile = async (file: File): Promise<void> => {
    setUploadError(null);
    setParsed(null);
    try {
      const text = await file.text();
      const parsedSheet = parseDelimitedText(text, file.name);
      if (parsedSheet.rows.length === 0) {
        setUploadError("That file is empty - no rows were found.");
        return;
      }
      setParsed(parsedSheet);
      const labelGuess =
        parsedSheet.headers.find((header) =>
          /label|name|date|week|month|period|x/i.test(header),
        ) ??
        parsedSheet.headers[0] ??
        "";
      const valueGuess =
        parsedSheet.headers.find((header) =>
          /value|amount|y|count|metric|kpi/i.test(header),
        ) ??
        parsedSheet.headers[1] ??
        parsedSheet.headers[0] ??
        "";
      setLabelCol(labelGuess);
      setValueCol(valueGuess);
    } catch (err) {
      console.warn(
        "[RunChartExpandedEditor] could not parse uploaded file",
        err,
      );
      setUploadError(
        "We could not read that file. Try a CSV or TSV with a header row.",
      );
    }
  };

  const confirmMapping = (): void => {
    if (!parsed) return;
    const points = parsed.rows
      .map((row) => {
        const labelRaw = row[labelCol];
        const valueRaw = row[valueCol];
        const label = labelRaw == null ? "" : String(labelRaw);
        const value =
          typeof valueRaw === "number"
            ? valueRaw
            : Number(String(valueRaw ?? "").replace(/[^0-9.-]/g, ""));
        return { id: newWidgetItemId("point"), label, value };
      })
      .filter((point) => point.label !== "" && Number.isFinite(point.value));

    if (points.length === 0) {
      setUploadError(
        "No usable rows once mapped. Pick a column with numeric values.",
      );
      return;
    }

    setDraft((current) => ({ ...current, points }));
    setParsed(null);
    setLabelCol("");
    setValueCol("");
    setUploadError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const cancelMapping = (): void => {
    setParsed(null);
    setLabelCol("");
    setValueCol("");
    setUploadError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onPasteRows = (event: ClipboardEvent<HTMLDivElement>): void => {
    if (readOnly) return;
    const raw = event.clipboardData.getData("text/plain");
    if (!raw.includes("\n") && !raw.includes("\t")) return;
    const rows = raw
      .trim()
      .split(/\r?\n/)
      .map((line) => line.split("\t").map((cell) => cell.trim()))
      .filter((cols) => cols.length >= 2 && cols[0]);
    const points = rows
      .map(([label, value]) => ({
        id: newWidgetItemId("point"),
        label: label ?? "",
        value: Number(String(value ?? "").replace(/[^0-9.-]/g, "")),
      }))
      .filter((point) => point.label && Number.isFinite(point.value));
    if (points.length === 0) return;
    event.preventDefault();
    setDraft((current) => ({ ...current, points }));
  };

  return (
    <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(0,400px)_minmax(0,1fr)] gap-4 overflow-hidden">
      <div
        className="min-h-0 flex flex-col gap-3 overflow-y-auto pe-1"
        onPaste={onPasteRows}
      >
        <div className="grid grid-cols-1 gap-3">
          <Field label="Chart title">
            <LeasedInput
              type="text"
              targetKey={scalarTarget("title")}
              value={draft.title ?? ""}
              disabled={readOnly}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  title: event.target.value,
                }))
              }
              placeholder="e.g. Weekly supplier response time"
              className={inputClass}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Y-axis label / unit">
              <LeasedInput
                type="text"
                targetKey={scalarTarget("unit")}
                value={draft.unit ?? ""}
                disabled={readOnly}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    unit: event.target.value,
                  }))
                }
                placeholder="e.g. days"
                className={inputClass}
              />
            </Field>
            <Field label="Target value">
              <LeasedInput
                type="number"
                inputMode="decimal"
                targetKey={scalarTarget("target")}
                value={draft.target ?? ""}
                disabled={readOnly}
                onChange={(event) => {
                  const raw = event.target.value;
                  setDraft((current) => {
                    const next: RunChartParams = { ...current };
                    if (raw === "") delete next.target;
                    else next.target = Number(raw);
                    return next;
                  });
                }}
                placeholder="-"
                className={inputClass}
              />
            </Field>
          </div>
        </div>

        <div className="border-t border-pb-border pt-3">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-pb-text-muted">
              Data points
            </div>
            <div className="flex items-center gap-1">
              <DsTooltip
                content="Upload CSV or TSV"
                disabled={readOnly === true}
              >
                {/* eslint-disable-next-line ds/no-hand-built -- kept: a 30px upload action with a 10px inset; Button secondary sm is 28px with the top hairline, and no approved look row lists the change. */}
                <button
                  type="button"
                  disabled={readOnly}
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-pb-text bg-pb-card-elevated hover:pb-overlay-paint border border-pb-control-border rounded-md px-2.5 py-1.5 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                >
                  <Upload className="w-3.5 h-3.5" /> Upload
                </button>
              </DsTooltip>
              {/* eslint-disable-next-line ds/no-hand-built -- hidden native file picker behind the Upload button; the design system has no picker-only part */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.tsv,text/csv,text/tab-separated-values"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void handleFile(file);
                }}
              />
              <Button
                variant="soft"
                size="sm"
                disabled={readOnly}
                onClick={addRow}
              >
                <Plus className="w-3.5 h-3.5" /> Add row
              </Button>
            </div>
          </div>

          {parsed ? (
            <div className="rounded-md border border-pb-border bg-pb-card-elevated/60 p-3 mb-3 space-y-2.5">
              <div className="text-xs text-pb-text-muted">
                Map the columns from{" "}
                <span className="text-pb-text font-medium">
                  {parsed.fileName}
                </span>{" "}
                ({parsed.rows.length} rows):
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Label column">
                  {/* eslint-disable-next-line ds/no-hand-built -- kept: a native select on a translucent page fill with the OS arrow; NativeSelect is a 36px card-fill field with its own chevron, and no approved look row lists the change. */}
                  <select
                    value={labelCol}
                    onChange={(event) => setLabelCol(event.target.value)}
                    className={inputClass}
                  >
                    {parsed.headers.map((header) => (
                      <option key={header} value={header}>
                        {header}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Value column">
                  {/* eslint-disable-next-line ds/no-hand-built -- kept: a native select on a translucent page fill with the OS arrow; NativeSelect is a 36px card-fill field with its own chevron, and no approved look row lists the change. */}
                  <select
                    value={valueCol}
                    onChange={(event) => setValueCol(event.target.value)}
                    className={inputClass}
                  >
                    {parsed.headers.map((header) => (
                      <option key={header} value={header}>
                        {header}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="ghost" size="sm" onClick={cancelMapping}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" onClick={confirmMapping}>
                  Replace data
                </Button>
              </div>
            </div>
          ) : null}

          {uploadError ? (
            <div className="rounded-md border border-pb-red/40 bg-pb-red/10 text-pb-red-text text-dense-1 px-2.5 py-1.5 mb-3">
              {uploadError}
            </div>
          ) : null}

          {draft.points.length === 0 ? (
            <div className="text-xs text-pb-text-muted italic px-2 py-6 text-center border border-dashed border-pb-border rounded-md">
              Add a row, paste from a spreadsheet, or upload a CSV / TSV file.
            </div>
          ) : (
            // The points grid is a DataTable: its frame, header, cell and row
            // paint replace the hand-built table's (look row OK-48).
            <DataTable
              columns={[
                {
                  id: "label",
                  header: "Label",
                  cell: ({ point, index }) => (
                    <LeasedInput
                      type="text"
                      targetKey={pointTarget(point.id, "label")}
                      value={point.label}
                      disabled={readOnly}
                      onChange={(event) =>
                        updatePoint(index, { label: event.target.value })
                      }
                      className="w-full bg-transparent border border-transparent rounded px-1.5 py-1 text-pb-text focus-ring disabled:opacity-60"
                    />
                  ),
                },
                {
                  id: "value",
                  header: "Value",
                  width: 100,
                  cell: ({ point, index }) => (
                    <LeasedInput
                      type="number"
                      inputMode="decimal"
                      targetKey={pointTarget(point.id, "value")}
                      value={Number.isFinite(point.value) ? point.value : ""}
                      disabled={readOnly}
                      onChange={(event) =>
                        updatePoint(index, {
                          value:
                            event.target.value === ""
                              ? 0
                              : Number(event.target.value),
                        })
                      }
                      className="w-full bg-transparent border border-transparent rounded px-1.5 py-1 text-pb-text focus-ring tabular-nums disabled:opacity-60"
                    />
                  ),
                },
                {
                  id: "delete",
                  header: "",
                  width: 44,
                  cell: ({ index }) => (
                    // The hint repeats the button's name, so it describes
                    // nothing more. A flex (block) wrapper keeps the button
                    // off the cell's text baseline, so the icon sits on the
                    // row's centre.
                    <DsTooltip
                      content="Delete row"
                      describeChild={false}
                      disabled={readOnly === true}
                      wrapperClassName="flex"
                    >
                      {/* eslint-disable-next-line ds/no-hand-built -- kept: a 22px delete icon with a 4px corner and a red hover wash; Button ghost critical xs is 24px with the 8px corner, and no approved look row lists the change. */}
                      <button
                        type="button"
                        disabled={readOnly}
                        onClick={() => deleteRow(index)}
                        className="p-1 rounded text-pb-text-muted transition-colors hover:bg-pb-red/10 hover:text-pb-red-text focus-ring disabled:opacity-50 disabled:pointer-events-none"
                        aria-label={`Delete row ${index + 1}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </DsTooltip>
                  ),
                },
              ]}
              data={draft.points.map((point, index) => ({ point, index }))}
              getRowId={({ point, index }) =>
                point.id ?? `${point.label}-${index}`
              }
              density="compact"
              textSize="small"
              layout="fixed"
              caption="Data points"
            />
          )}
        </div>
      </div>

      <div className="min-h-0 flex flex-col rounded-md border border-pb-border bg-pb-card-elevated/40 p-4">
        <RunChartPreview
          chartData={draft.points}
          unit={draft.unit ?? ""}
          target={draft.target}
          title={draft.title?.trim() || "Run Chart"}
          lastPoint={lastPoint}
          targetState={targetState}
        />
      </div>
    </div>
  );
}

function RunChartPreview({
  chartData,
  unit,
  target,
  title,
  lastPoint,
  targetState,
}: {
  readonly chartData: ReadonlyArray<{
    readonly label: string;
    readonly value: number;
  }>;
  readonly unit: string;
  readonly target?: number | undefined;
  readonly title: string;
  readonly lastPoint?:
    | { readonly label: string; readonly value: number }
    | undefined;
  readonly targetState: ReturnType<typeof runChartTargetState>;
}): ReactElement {
  const reducedMotion = useReducedMotion();
  const gradientId = `run-chart-expanded-${useId().replace(/:/g, "")}`;
  if (chartData.length === 0) {
    return (
      <div className="flex-1 grid place-items-center text-pb-text-muted text-xs italic px-4 text-center">
        Add a row, paste from a spreadsheet, or upload a CSV / TSV file to see
        the trend.
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-2">
      <div className="text-pb-text font-semibold text-sm tracking-tight">
        {title}
      </div>
      <div className="flex items-baseline justify-between shrink-0">
        <KPIValue
          size="lg"
          value={lastPoint?.value}
          unit={unit}
          deltaSlot={
            target != null ? (
              <span
                className={cn(
                  "ms-1 text-dense-2 uppercase tracking-wider font-semibold py-0.5 px-1.5 rounded-md border",
                  "border-pb-border bg-pb-card-elevated text-pb-text-muted",
                )}
              >
                {targetState?.label}
              </span>
            ) : null
          }
        />
        <span className="text-dense-1 text-pb-text-muted">
          {chartData[0]?.label} - {lastPoint?.label}
        </span>
      </div>
      <div className="flex-1 min-h-0">
        {/* Kept hand-built: recharts chart marks, which no design-system part draws. */}
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={[...chartData]}
            margin={{ top: 8, right: 24, bottom: 4, left: 0 }}
          >
            {target !== undefined ? (
              <defs>
                <RunChartTargetGradient
                  id={gradientId}
                  points={chartData}
                  target={target}
                />
              </defs>
            ) : null}
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="hsl(var(--pb-border))"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "hsl(var(--pb-text-muted))" }}
              axisLine={{ stroke: "hsl(var(--pb-border))" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "hsl(var(--pb-text-muted))" }}
              axisLine={false}
              tickLine={false}
              width={40}
            />
            <Tooltip
              cursor={{ stroke: "hsl(var(--pb-border))", strokeWidth: 1 }}
              content={
                <ChartTooltip
                  formatValue={(value) => `${String(value)} ${unit}`}
                />
              }
            />
            {target != null ? (
              <ReferenceLine
                y={target}
                stroke="hsl(var(--pb-amber))"
                strokeDasharray="4 4"
                label={{
                  value: `Target ${target}`,
                  position: "right",
                  fontSize: 10,
                  fill: "hsl(var(--pb-amber))",
                }}
              />
            ) : null}
            <Line
              type="monotone"
              dataKey="value"
              name="Value"
              stroke={
                target === undefined
                  ? "hsl(var(--pb-cyan))"
                  : `url(#${gradientId})`
              }
              strokeWidth={2.25}
              dot={(props) => {
                const { key, ...dotProps } = props;
                return (
                  <RunChartTargetDot key={key} {...dotProps} target={target} />
                );
              }}
              activeDot={(props: {
                cx?: number;
                cy?: number;
                payload?: { value?: number };
              }) => (
                <RunChartTargetDot
                  {...props}
                  {...(target === undefined ? {} : { target })}
                  radius={5}
                />
              )}
              isAnimationActive={!reducedMotion}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function parseDelimitedText(text: string, fileName: string): ParsedSheet {
  const delimiter = text.includes("\t") ? "\t" : ",";
  const rows = text
    .split(/\r?\n/)
    .map((line) => splitDelimitedLine(line, delimiter))
    .filter((cols) => cols.some((cell) => cell.trim() !== ""));
  const headers =
    rows[0]?.map((header, index) => header.trim() || `Column ${index + 1}`) ??
    [];
  if (headers.length === 0) return { headers: [], rows: [], fileName };
  const dataRows = rows.slice(1).map((cols) => {
    const row: Record<string, string | number> = {};
    headers.forEach((header, index) => {
      const raw = cols[index] ?? "";
      const numeric = Number(raw);
      row[header] =
        raw.trim() !== "" && Number.isFinite(numeric) ? numeric : raw;
    });
    return row;
  });
  return { headers, rows: dataRows, fileName };
}

function splitDelimitedLine(line: string, delimiter: string): string[] {
  if (delimiter === "\t") return line.split("\t");
  const cells: string[] = [];
  let current = "";
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      quoted = !quoted;
    } else if (char === "," && !quoted) {
      cells.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  cells.push(current.trim());
  return cells;
}

/**
 * PB-4923 AC5 — the canonical keys this editor's controls lease.
 *
 * Built with `widgetTargetKey` rather than a template string: it is the same
 * grammar the executor, the revision map, the edit-session table and the audit
 * all key on, and a hand-rolled key that drifted from it would fail SILENTLY —
 * leasing a target no operation ever names looks exactly like working.
 */
function scalarTarget(name: "title" | "unit" | "target"): string {
  return widgetTargetKey([{ kind: "prop", name }]);
}

/**
 * A point minted before the id migration has no `id` and is therefore not
 * addressable. It leases NOTHING, which is the honest answer — see
 * `LeasedInput.targetKey`.
 */
function pointTarget(
  pointId: string | undefined,
  name: "label" | "value",
): string | undefined {
  if (pointId === undefined) return undefined;
  return widgetTargetKey([
    { kind: "item", collection: "points", itemId: pointId },
    { kind: "prop", name },
  ]);
}

function Field({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}): ReactElement {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-dense-2 uppercase tracking-wider font-semibold text-pb-text-muted">
        {label}
      </span>
      {children}
    </label>
  );
}

const inputClass =
  "w-full bg-pb-page/40 border border-pb-border rounded-md px-2.5 py-1.5 text-sm text-pb-text focus-ring placeholder:text-pb-text-muted/70 disabled:opacity-60 disabled:cursor-not-allowed";
